import React from 'react';
import { useEdgeEventData } from './hooks/useEdgeEventData';
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import { useAutoLoad } from '../../../hooks/useAutoLoad';
import { cn } from '../../../utils/cn';
import { useGlobalAPI } from '../../../hooks/useGlobalAPI';
import { useAutoFitFontSize } from '../_shared/useAutoFitFontSize';

const METRIC_BLOCK_VALUE_MAX_PX = 72; // was text-7xl
const METRIC_BLOCK_VALUE_MIN_PX = 20;

// MetricBlock Component - Updated with Tailwind
const MetricBlock = ({ attrName, fileData }) => {
  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '—';
    return (value * 100).toFixed(1) + '%';
  };

  const hasError = !fileData || fileData.error;
  const hasData = fileData && (fileData.found || !fileData.error);
  const percentage = (fileData && fileData.ratio !== null && fileData.ratio !== undefined)
    ? fileData.ratio : null;
  const valueText = !hasData || percentage === null ? '—' : formatPercent(percentage);

  const { ref, fontSizePx } = useAutoFitFontSize({
    text: hasError ? '' : valueText,
    maxPx: METRIC_BLOCK_VALUE_MAX_PX,
    minPx: METRIC_BLOCK_VALUE_MIN_PX,
  });

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-center flex-1 min-w-[150px] max-w-[220px] shadow-sm flex flex-col items-center justify-center">
      <h4 className="text-base font-semibold text-gray-800 mb-2">
        {attrName}
      </h4>
    <div className="rounded-md p-4 text-center flex-1 min-w-[150px] max-w-[220px] flex flex-col items-center justify-center min-h-[120px]">
      <div className={cn(
        hasError
          ? "text-error-600 text-lg italic"
          : "text-primary-500 font-bold mb-2"
      )}>
        {hasError ? (
          <span className="text-lg italic">{fileData?.error || 'Error'}</span>
        ) : (
          <span ref={ref} className="max-w-full truncate inline-block" style={{ fontSize: `${fontSizePx}px` }}>
            {valueText}
          </span>
        )}
      </div>
      {hasData && !hasError && fileData.count !== null && (
        <div className="text-sm text-gray-500 text-center">
          (Events: {fileData.count} / {fileData.totalAuftrag})
          {fileData.filtered && (
            <div className="text-sm text-blue-600 mt-1 italic">
              Filtered
            </div>
          )}
        </div>
      )}
    </div>
  </div>
  );
};

const EdgeFrequency = ({
  targetEdge = "Auftragsbestätigung senden → Terminierung durch MM",
  label = "Delivery Date Change Rate",
  attributesToShow = ["main", "gemu", "M88", "MSET", "MDOKU"],
  activeFile,
  onRefresh
}) => {
  // ✅ NEW: Use unified API hook - automatically applies global filters
  const api = useGlobalAPI(activeFile);
  const { edgeData, isLoading, error, hasFetched, hasData, loadData } = useEdgeEventData(targetEdge, activeFile, api);
  
  // Auto-load data when persistent cache is enabled
  useAutoLoad(loadData, [activeFile, targetEdge], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(loadData);
    }
  }, [onRefresh, loadData]);

  // Map activeFile to display format and determine which data to show
  const getFileDisplayData = (activeFile, allData) => {
    if (!allData || Object.keys(allData).length === 0) return {};
    
    // Map activeFile values to the keys used in the API response
    const fileMapping = {
      'main': 'main',
      'gemu': 'gemu',
      'm88': 'M88',
      'mset': 'MSET',
      'mdoku': 'MDOKU'
    };
    
    const targetKey = fileMapping[activeFile];
    
    // If activeFile is specified and exists in data, show only that file
    if (activeFile && targetKey && allData[targetKey]) {
      return { [targetKey]: allData[targetKey] };
    }
    
    // Fallback: show all data if activeFile not found or not specified
    return allData;
  };
  
  const displayData = getFileDisplayData(activeFile, edgeData);
  const displayKeys = Object.keys(displayData);

  const showInitialButton = !hasFetched && !isLoading;
  const showData = hasFetched && !isLoading && !error && hasData;
  const showNoDataMessage = hasFetched && !isLoading && !error && !hasData;

  return (
    <BaseTile
      minHeight="auto"
      padding="sm"
      className=""
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Initial Load Button */}
        {showInitialButton && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Load Data
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <TileStatusContent status="loading" />
        )}

        {/* Error State with Retry */}
        {!isLoading && error && (
          <TileStatusContent
            status="error"
            title="Failed to load edge event data"
            message={error}
            action={
              <button
                onClick={loadData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry
              </button>
            }
          />
        )}

        {/* Data Display */}
        {showData && displayKeys.length > 0 && (
          <div className="flex flex-col gap-4 w-full">
            {activeFile ? (
              // Show single file data when activeFile is specified
              <div className="flex flex-row gap-6 py-2 flex-wrap justify-center w-full">
                {displayKeys.map(fileKey => (
                  <MetricBlock 
                    key={fileKey} 
                    attrName={fileKey === 'main' ? '' : fileKey === 'gemu' ? 'Gemü' : `${fileKey} Dataset`}
                    fileData={displayData[fileKey]} 
                  />
                ))}
              </div>
            ) : (
              // Show all files when no activeFile specified (fallback)
              <div className="flex flex-row gap-6 py-2 flex-wrap justify-around w-full">
                {displayKeys.map(fileKey => (
                  <MetricBlock 
                    key={fileKey} 
                    attrName={fileKey}
                    fileData={displayData[fileKey]} 
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* No Data Message */}
        {showNoDataMessage && (
          <TileStatusContent
            status="empty"
            title="No Edge Event Data Available"
            message="No data matches the current configuration."
            action={
              <button
                onClick={loadData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry
              </button>
            }
          />
        )}
      </TileContent>
    </BaseTile>
  );
};

export default EdgeFrequency;